import type { DateRangeValue } from "@/components/ui/date-range-picker";
import { lastMonthsRange } from "@/lib/date-utils";
import {
  AGREEMENT_FILTER_OPTIONS,
  ALL_AGREEMENTS,
  type AgreementFilterValue,
} from "@/services/admin-agreement.service";
import type { TenantStatus } from "@/services/admin-tenant.service";
import { createTableFiltersStore } from "./createTableFiltersStore";

/**
 * Sentinel for "no status filter".
 *
 * Radix Select throws on a `<SelectItem value="">` and treats `""` as "nothing selected", so the
 * cleared state needs a real value. No TenantStatus member is `'all'`, so this cannot collide with
 * a genuine status.
 */
export const ALL_STATUSES = "all";

/** Statuses tenants can actually reach — see TenantFilters for why Pending/Inactive are absent. */
export const TENANT_STATUS_OPTIONS: {
  value: TenantStatus | typeof ALL_STATUSES;
  label: string;
}[] = [
  { value: ALL_STATUSES, label: "All Statuses" },
  { value: "Active", label: "Active" },
  { value: "Suspended", label: "Suspended" },
];

export interface TenantListFilters extends Record<string, unknown> {
  /** Free-text term: tenant name, tenant id, or the owner's email / phone. */
  search: string;

  status: TenantStatus | typeof ALL_STATUSES;

  /** Created-at range, ISO strings. Sent as createdFromFilter / createdToFilter. */
  dateRange: DateRangeValue;

  /** Commitment and whether the tenant agreed. Sent as agreementTypeFilter / agreementAcceptedFilter. */
  agreement: AgreementFilterValue;
}

/**
 * Both ends of the created-at range default to the last six months rather than to "no filter": the
 * list opens on a bounded window, and the operator widens or clears it from the picker. Computed
 * once per page load, so a tab left open for days keeps the window it started with until the
 * filters are reset.
 */
/**
 * True when a stored range has exactly one end. The picker only ever produces both or neither, so
 * this can only come from hand-edited storage.
 */
const isHalfRange = (range: DateRangeValue | undefined): boolean =>
  Boolean(range?.from) !== Boolean(range?.to);

const defaults: TenantListFilters = {
  search: "",
  status: ALL_STATUSES,
  dateRange: lastMonthsRange(),
  agreement: ALL_AGREEMENTS,
};

/**
 * Filter and paging state for the admin tenant list, cached in sessionStorage.
 *
 * `defaultSort` is deliberately omitted: the table has no sort control, so `sort` stays null and
 * the query sends no ordering — the backend's newest-first default is the order. That also makes a
 * `sort` left in an operator's localStorage by an earlier release inert rather than a hidden
 * ordering they cannot change, which is why removing it needs no version bump.
 *
 * Bump `version` in step with any change to the shape above or to the status options — a cached
 * value the backend no longer accepts comes back as a 400 on a filter the operator cannot see.
 */
export const useTenantFiltersStore = createTableFiltersStore<TenantListFilters>(
  {
    name: "admin.tenants.filters",
    version: 3,
    defaults,
    sanitize: (filters) => ({
      ...filters,

      // sessionStorage outlives a release: a status that is no longer offered must not survive as a
      // filter the operator cannot see in the UI, and so cannot clear from it.
      status: TENANT_STATUS_OPTIONS.some(
        (option) => option.value === filters.status,
      )
        ? filters.status
        : defaults.status,

      // Exactly one end is not a range: the query applies createdFrom/createdTo together, so a
      // stored half would show as a filter chip that narrows nothing. An *empty* range is left alone —
      // it is the operator having deliberately cleared the six-month default to search all time, and
      // replacing it here would silently put the default back on every reload.
      dateRange: isHalfRange(filters.dateRange)
        ? defaults.dateRange
        : filters.dateRange,

      // Same reason as status: a commitment choice no longer offered falls back to "all".
      agreement: AGREEMENT_FILTER_OPTIONS.some(
        (option) => option.value === filters.agreement,
      )
        ? filters.agreement
        : defaults.agreement,
    }),
  },
);
